import { Map, List, fromJS } from "immutable";
import { selectData } from "fe-shared/utils/reducerUtils";

import { messageStatus, messageTypes } from "constants";
import {
  isActionTypeWait,
  isActionPending,
  isChoiceType,
  isMessageFromBot,
  isFromHuman,
  parseNumberToString,
} from "./utils";
import {
  STATE_WEBBOT,
  STATE_WEBBOT_PREVIEWS,
  STATE_WEBBOT_MESSAGES,
  STATE_WEBBOT_MESSAGE,
  STATE_WEBBOT_ACTIONS,
  STATE_WEBBOT_IS_TYPING,
  STATE_WEBBOT_URLS,
  STATE_WEBBOT_REQUEST_URL,
  STATE_WEBBOT_TYPE,
  STATE_WEBBOT_CONNECTIONS,
  STATE_WEBBOT_ID_MESSAGE_EVALUATE,
  STATE_WEBBOT_FROM,
  STATE_WEBBOT_SHOW_AVATAR,
  STATE_WEBBOT_STATUS,
  STATE_WEBBOT_OPTIONS_TYPE,
  STATE_WEBBOT_OPTIONS,
  STATE_WEBBOT_ARE_BUTTONS_VISIBLE,
  STATE_WEBBOT_ID,
  STATE_WEBBOT_IS_RESTARTING_CHAT,
  STATE_WEBBOT_CVARS_SAVED,
  STATE_WEBBOT_SDK_TYPE,
  STATE_WEBBOT_SKIP_USER_INPUT,
} from "./default";

export const getWebbot = state => selectData(state, STATE_WEBBOT);
export const getWebbotMessagesRaw = state =>
  selectData(getWebbot(state), STATE_WEBBOT_MESSAGES);
export const getPreviews = state =>
  selectData(getWebbot(state), STATE_WEBBOT_PREVIEWS);
export const getWebbotIsTyping = state =>
  selectData(getWebbot(state), STATE_WEBBOT_IS_TYPING);
export const getWebbotIsRestartingChat = state =>
  selectData(getWebbot(state), STATE_WEBBOT_IS_RESTARTING_CHAT);
export const getCVarsValue = state =>
  selectData(getWebbot(state), STATE_WEBBOT_CVARS_SAVED);

const markSuggestedRepliesListButtonVisibility = messages => {
  const lastMessageIndex = messages.size - 1;
  const areMultipleMessages = messages.size > 2;

  let isLastActionOfSdkType = false;

  // One human message is sent by default so when bot speaks, there are always two messages min.
  if (areMultipleMessages) {
    const formattedMessages = messages.toJS();
    const lastMessage = formattedMessages[lastMessageIndex];
    const {
      message: { actions: lastActions },
    } = lastMessage;
    const lastAction = lastActions && lastActions[lastActions.length - 1];

    isLastActionOfSdkType =
      lastAction && lastAction.type === STATE_WEBBOT_SDK_TYPE;
  }

  return messages.map((message, messageIndex) => {
    const actions = selectData(
      selectData(message, STATE_WEBBOT_MESSAGE),
      STATE_WEBBOT_ACTIONS
    );
    const isLastMessage = messageIndex === lastMessageIndex;
    const lastActionIndex = actions.size - 1;

    const modifiedActions = actions.map((action, index) => {
      const isLastAction = lastActionIndex === index;
      const actionType = selectData(action, STATE_WEBBOT_TYPE);

      if (isChoiceType(actionType)) {
        const isChoiceSecondLastMessage = lastMessageIndex - 1 === messageIndex;
        const optionsType = selectData(action, STATE_WEBBOT_OPTIONS_TYPE);
        const isAlwaysVisible = optionsType === STATE_WEBBOT_OPTIONS;
        const isLastActionOfLastMessage = isLastMessage && isLastAction;
        const areButtonsVisible =
          isLastActionOfLastMessage ||
          isAlwaysVisible ||
          (isLastActionOfSdkType && isChoiceSecondLastMessage);

        return action.set(STATE_WEBBOT_ARE_BUTTONS_VISIBLE, areButtonsVisible);
      }
      return action;
    });

    return message.setIn(
      [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
      modifiedActions
    );
  });
};

const populateWithPreviews = (state, messages) => {
  const previews = getPreviews(state);

  return messages.map(message =>
    message.updateIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS], actions =>
      actions.map(action => {
        const urls = selectData(action, STATE_WEBBOT_URLS);
        if (urls) {
          const actionPreviews = urls
            .map(url =>
              previews.find(
                el => selectData(el, STATE_WEBBOT_REQUEST_URL) === url
              )
            )
            .filter(preview => preview);

          if (actionPreviews.size > 0) {
            return action.set(STATE_WEBBOT_PREVIEWS, actionPreviews);
          }
        }

        return action;
      })
    )
  );
};

export const getWebbotMessages = state => {
  const messages = getWebbotMessagesRaw(state);
  return populateWithPreviews(
    state,
    markSuggestedRepliesListButtonVisibility(messages)
  );
};

export const getFirstPendingWait = state => {
  const messages = getWebbotMessages(state);
  let result = false;
  messages.forEach(message => {
    const actions = message.getIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS]);
    const wait = actions.find(
      action => isActionTypeWait(action) && isActionPending(action)
    );

    if (wait) {
      result = result || wait;
    }
  });

  return result;
};

const splitSuggestedRepliesIntoButtonsAndText = messageList =>
  messageList.map(message =>
    message.updateIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS], actions =>
      actions.reduce((acc, action) => {
        if (
          isChoiceType(selectData(action, STATE_WEBBOT_TYPE)) &&
          selectData(action, messageTypes.text) !== "…"
        ) {
          return acc.push(
            fromJS({
              type: messageTypes.text,
              text: selectData(action, messageTypes.text),
            }),
            action.set(messageTypes.text, "…")
          );
        }

        return acc.push(action);
      }, List())
    )
  );

const markMessageWithBotAvatar = messageList => {
  let avatarShown = false;
  return splitSuggestedRepliesIntoButtonsAndText(messageList)
    .reverse()
    .map(message => {
      const isBot = isMessageFromBot(message);
      if (isBot && !avatarShown) {
        return message.updateIn(
          [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
          actions =>
            actions
              .reverse()
              .map(action => {
                if (isChoiceType(selectData(action, STATE_WEBBOT_TYPE))) {
                  return action.set(STATE_WEBBOT_SHOW_AVATAR, false);
                }

                const result = action.set(
                  STATE_WEBBOT_SHOW_AVATAR,
                  !avatarShown
                );
                if (!avatarShown) {
                  avatarShown = true;
                }

                return result;
              })
              .reverse()
        );
      }

      avatarShown = false;

      return message;
    })
    .reverse();
};

export const getWebbotMessagesForDebuggerMode = state => {
  const messages = getWebbotMessages(state);

  const getNextConnections = ({ nextKey, id }) => {
    let nextConnections = List();
    let messageId = 0;
    if (nextKey < messages.size) {
      const nextMessage = messages.getIn(
        [nextKey, STATE_WEBBOT_MESSAGE],
        Map()
      );
      messageId = selectData(nextMessage, STATE_WEBBOT_ID, 0);
      nextConnections = selectData(
        nextMessage,
        STATE_WEBBOT_CONNECTIONS,
        List()
      );
      const isSameId =
        parseNumberToString(
          selectData(nextConnections, STATE_WEBBOT_ID_MESSAGE_EVALUATE, -1)
        ) === id;
      if (!isSameId) {
        const key = nextKey + 1;
        return getNextConnections({ nextKey: key, id });
      }
    }
    return nextConnections.set("messageId", messageId);
  };

  const getConversationByModules = allMessages => {
    let messagesByModules = List();
    const joinedIndex = [];
    allMessages.forEach((message, index) => {
      if (!joinedIndex.includes(index)) {
        const botAndHuman = List();
        const isFromBot = isMessageFromBot(message);
        if (isFromBot) {
          const nextKey = index + 1;
          const nextMessage = allMessages.getIn(
            [nextKey, STATE_WEBBOT_MESSAGE],
            Map()
          );
          const isNextMessageFromHuman = isFromHuman(
            selectData(nextMessage, STATE_WEBBOT_FROM, false)
          );
          if (isNextMessageFromHuman) {
            joinedIndex.push(nextKey);
            messagesByModules = messagesByModules.push(
              botAndHuman.push(message).push(nextMessage)
            );
          } else if (
            message.getIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS], List())
              .size === 0 &&
            message.getIn(
              [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_SKIP_USER_INPUT],
              false
            )
          ) {
            messagesByModules = messagesByModules.push(message);
          } else {
            messagesByModules = messagesByModules.push(message);
          }
        } else {
          messagesByModules = messagesByModules.push(message);
        }
      }
    });

    return messagesByModules;
  };

  const result = messages.map((message, key) =>
    message
      .updateIn(
        [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
        actions =>
          actions &&
          actions.filter(
            action =>
              selectData(action, STATE_WEBBOT_STATUS, false) ===
              messageStatus.visible
          )
      )
      .updateIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_CONNECTIONS], () => {
        const nextKey = key + 1;
        const id = parseNumberToString(
          message.getIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ID], null)
        );
        return getNextConnections({ nextKey, id });
      })
  );
  return getConversationByModules(markMessageWithBotAvatar(result));
};

export const getWebbotVisibleMessages = state =>
  markMessageWithBotAvatar(
    getWebbotMessages(state)
      .map(message =>
        message.updateIn(
          [STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS],
          actions =>
            actions &&
            actions.filter(
              action =>
                selectData(action, STATE_WEBBOT_STATUS, false) ===
                messageStatus.visible
            )
        )
      )
      .filter(
        message =>
          message.getIn([STATE_WEBBOT_MESSAGE, STATE_WEBBOT_ACTIONS]).size > 0
      )
  );
